import { Button as ButtonPrimitive } from "@base-ui/react/button"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-lg border border-transparent bg-clip-padding text-sm [font-weight:var(--brand-button-weight,500)] [text-transform:var(--brand-button-transform,none)] whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary-hover",
        outline:
          "border-border bg-background hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        ghost:
          "hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50",
        // Blackline M3a (#2693): the tinted destructive style sits on the opaque
        // danger ground (4.66:1 dark, 4.98:1 light under --destructive) instead
        // of the alpha tint, which composited to 4.25:1 and 4.19:1 and failed
        // AA on the preview; hover is a border, so the ground never changes.
        destructive:
          "bg-[var(--danger-bg)] text-destructive hover:border-destructive/40 aria-expanded:border-destructive/40 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40",
        // #3301: an affirmative move forward (green) and the hired outcome
        // (blue), each on its own token pair like destructive, so neither
        // follows the tenant accent. The hover is an opaque token, so the
        // label keeps its measured contrast; the label always carries the
        // meaning and the colour only reinforces it.
        //
        // The focus ring is drawn in the theme's FOREGROUND ink, at full
        // opacity, offset onto the canvas. The kit's ring is the tenant accent
        // at half alpha: against the affirmative fill it vanishes, and offset
        // onto a light canvas it measures about 1.5:1. Ink on the canvas is
        // near-black on light and near-white on dark, so the indicator holds
        // in both themes whatever the tenant's accent is.
        affirmative:
          "bg-affirmative text-affirmative-foreground hover:bg-affirmative-hover aria-expanded:bg-affirmative-hover focus-visible:ring-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        hired:
          "bg-hired text-hired-foreground hover:bg-hired-hover aria-expanded:bg-hired-hover focus-visible:ring-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        link: "text-accent-strong underline-offset-4 hover:underline",
        // Blackline M3a (#2693): the brand fill is reserved for rare high-value
        // actions (book the call, submit the test). Routine actions stay on
        // `default`, which turns neutral in M2b.
        accent:
          "bg-brand text-brand-foreground hover:bg-brand/90 aria-expanded:bg-brand/90",
      },
      size: {
        default:
          "h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        xs: "h-6 gap-1 rounded-[min(var(--radius-md),10px)] px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 gap-1 rounded-[min(var(--radius-md),12px)] px-2.5 text-[0.8rem] in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        // Blackline M3a (#2693), Decision 4: the 44 px control is for learner
        // and auth surfaces only, so it is a new size rather than a change to
        // `lg`, which admin tables still use at 36 px.
        xl: "h-11 gap-2 px-4 text-base has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3 [&_svg:not([class*='size-'])]:size-5",
        icon: "size-8",
        "icon-xs":
          "size-6 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
        "icon-sm":
          "size-7 rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg",
        "icon-lg": "size-9",
        "icon-xl": "size-11 [&_svg:not([class*='size-'])]:size-5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
